import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Check, ExternalLink, X, XCircle } from 'lucide-react';
import type { JourneyGraph, RecommendationDto } from '@ederest/contracts';
import { ApiError } from '../../shared/api/client.js';
import {
  useGuidanceActions,
  useJourneyGraph,
  useRecommendation,
  useRecommendationDecision,
} from '../../shared/api/queries.js';
import { DismissDialog } from '../../shared/guidance/DismissDialog.js';
import { GuidanceCard } from '../../shared/guidance/GuidanceCard.js';
import {
  DISMISSAL_REASON_LABEL,
  PLATFORM_CAUSE_LABEL,
  RECOMMENDATION_STATE_LABEL,
  formatDateTime,
} from '../../shared/guidance/labels.js';
import { Badge } from '../../shared/ui/Badge.js';
import { Button } from '../../shared/ui/Button.js';
import { Card } from '../../shared/ui/Card.js';
import { FrictionScore } from '../../shared/ui/FrictionScore.js';
import { PRIORITY_HINT, isPriorityKnown, targetOf } from './recommendation-reading.js';
import { QUEUE_COLUMN_HINTS } from './RecommendationQueue.js';

const LANGUAGES = ['fr', 'ar'] as const;

interface RecommendationDetailProps {
  recommendationId: string;
  onClose: () => void;
}

/**
 * One recommendation: the figures that justify it (functional §5.6 "motif
 * chiffré"), the expected effect, then the guidance it produced with every
 * decision of functional §8.4 — or, for a site change, the decision alone.
 */
export function RecommendationDetail({ recommendationId, onClose }: RecommendationDetailProps) {
  const { data: recommendation, isLoading, isError } = useRecommendation(recommendationId);
  const { data: graph } = useJourneyGraph(recommendation?.journeyId);
  const actions = useGuidanceActions();
  const decide = useRecommendationDecision();
  const [dismissing, setDismissing] = useState(false);

  if (isLoading) return <Card title="Recommandation">Chargement…</Card>;
  if (isError || !recommendation) {
    return (
      <Card
        title="Recommandation"
        actions={<Button variant="ghost" icon={<X />} aria-label="Fermer le détail" onClick={onClose} />}
      >
        Cette recommandation n'existe pas ou n'est pas dans votre périmètre.
      </Card>
    );
  }
  return (
    <Loaded
      recommendation={recommendation}
      graph={graph}
      actions={actions}
      decide={decide}
      dismissing={dismissing}
      setDismissing={setDismissing}
      onClose={onClose}
    />
  );
}

interface LoadedProps {
  recommendation: RecommendationDto;
  graph: JourneyGraph | undefined;
  actions: ReturnType<typeof useGuidanceActions>;
  decide: ReturnType<typeof useRecommendationDecision>;
  dismissing: boolean;
  setDismissing: (value: boolean) => void;
  onClose: () => void;
}

function Loaded({ recommendation, graph, actions, decide, dismissing, setDismissing, onClose }: LoadedProps) {
  const state = RECOMMENDATION_STATE_LABEL[recommendation.state];
  const evidence = recommendation.evidence;
  const guidance = recommendation.guidance;

  return (
    <Card
      className="detail-card"
      title={`${recommendation.journeyName} › ${recommendation.stepName}`}
      actions={
        <span className="detail-card__actions">
          <Badge tone={state.tone}>{state.text}</Badge>
          <Button variant="ghost" icon={<X />} aria-label="Fermer le détail" onClick={onClose} />
        </span>
      }
    >
      <div className="detail-card__body" data-testid="recommendation-detail">
        <dl className="evidence">
          <Figure
            label="Friction"
            hint={QUEUE_COLUMN_HINTS.friction}
            value={<FrictionScore score={evidence.frictionScore} />}
          />
          <Figure
            label="Priorité"
            hint={PRIORITY_HINT}
            value={isPriorityKnown(recommendation) ? String(recommendation.priorityScore) : 'Sans avis'}
          />
          <Figure
            label="Usagers concernés"
            hint={QUEUE_COLUMN_HINTS.users}
            value={evidence.affectedSessions.toLocaleString('fr-FR')}
          />
          <Figure
            label="Abandons"
            hint="Usagers partis du site à cet endroit sans terminer leur démarche."
            value={evidence.abandonCount.toLocaleString('fr-FR')}
          />
          <Figure
            label="Erreurs"
            hint="Messages d'erreur affichés par le formulaire à cet endroit."
            value={evidence.errorCount.toLocaleString('fr-FR')}
          />
          <Figure
            label="Hésitations"
            hint="Arrêts prolongés sur un champ sans rien saisir (8 secondes par défaut)."
            value={evidence.hesitationCount.toLocaleString('fr-FR')}
          />
        </dl>
        <p className="detail-card__target">
          {targetOf(recommendation)} · page{' '}
          <code title="Adresse de la page sur le portail">{evidence.pagePath}</code>
          {' · '}
          <Link to={`/journeys/${recommendation.journeyId}`} className="detail-card__link">
            Ouvrir le parcours <ExternalLink size={11} />
          </Link>
        </p>

        <section className="detail-card__section">
          <h4>Pourquoi cette proposition</h4>
          <p>
            {recommendation.rationale || (
              <em>
                Pas d'explication rédigée : l'intelligence artificielle était indisponible lors de l'analyse.
                Les chiffres ci-dessus restent valables.
              </em>
            )}
          </p>
          <h4>Effet attendu</h4>
          <p>{recommendation.expectedImpact}</p>
          {recommendation.platformCause && (
            <p>
              <Badge tone="teal">Modification du site</Badge>{' '}
              <strong>{PLATFORM_CAUSE_LABEL[recommendation.platformCause]}</strong> — à mettre en œuvre par le
              client ; documentée ici et dans l'export.
            </p>
          )}
        </section>

        {recommendation.decision && (
          <section
            className="detail-card__section detail-card__decision"
            data-testid="recommendation-decision"
          >
            <h4>Décision</h4>
            <p>
              <strong>{RECOMMENDATION_STATE_LABEL[recommendation.decision.outcome].text}</strong>
              {recommendation.decision.dismissalReason &&
                ` · ${DISMISSAL_REASON_LABEL[recommendation.decision.dismissalReason]}`}
              {' · '}
              {recommendation.decision.decidedByName ?? 'Compte inconnu'} ·{' '}
              {formatDateTime(recommendation.decision.decidedAt)}
            </p>
            {recommendation.decision.note && (
              <p className="detail-card__note">{recommendation.decision.note}</p>
            )}
          </section>
        )}

        {guidance ? (
          <GuidanceCard
            guidance={guidance}
            languages={LANGUAGES}
            steps={graph?.steps ?? []}
            actions={actions}
            contentGenerationSkipped={recommendation.contentGenerationSkipped}
            dismissalReason={recommendation.decision?.dismissalReason ?? null}
          />
        ) : (
          recommendation.state === 'pending' && (
            <footer className="detail-card__footer">
              <Button
                variant="ghost"
                icon={<XCircle />}
                onClick={() => setDismissing(true)}
                disabled={decide.isPending}
              >
                Écarter
              </Button>
              <Button
                variant="primary"
                icon={<Check />}
                disabled={decide.isPending}
                onClick={() => decide.mutate({ id: recommendation.id, decision: 'accept' })}
              >
                Accepter et transmettre
              </Button>
            </footer>
          )
        )}

        {decide.error instanceof ApiError && (
          <p className="guidance__warning" role="alert">
            {decide.error.status === 409 ? 'Cette recommandation a déjà été décidée.' : decide.error.message}
          </p>
        )}
      </div>

      {dismissing && (
        <DismissDialog
          title="Écarter cette recommandation"
          busy={decide.isPending}
          onCancel={() => setDismissing(false)}
          onConfirm={(reason, note) =>
            decide.mutate(
              { id: recommendation.id, decision: 'dismiss', reason, note },
              { onSettled: () => setDismissing(false) },
            )
          }
        />
      )}
    </Card>
  );
}

function Figure({ label, hint, value }: { label: string; hint: string; value: ReactNode }) {
  return (
    <div className="evidence__item" title={hint}>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}
